import { useEffect, useRef } from 'react';
import { Alert, Box, Link, SpaceBetween } from '@cloudscape-design/components';
import { PageLayout } from '@deloitte-us-consulting/gp-sdk';
import type { PluginEntry } from './registry';

/**
 * Landing page for a LINKED app's `/:appId` route. A linked app has no bundle —
 * it lives outside the platform with its own auth and hosting — so there is
 * nothing to mount. Attempting `RemoteMount` here would misreport it as
 * "headless" (no UI) or, worse, invite a federation load that has no
 * `remoteEntry.js` to fetch (the confusing "Failed to fetch dynamically
 * imported module" error a missing remote produces).
 *
 * Instead: open it in a new tab automatically, and render a page explaining
 * that plus a manual link in case the browser blocked the popup.
 */
export function LinkedAppRoute({ app }: { app: PluginEntry }) {
  const opened = useRef(false);

  useEffect(() => {
    if (opened.current || !app.externalUrl) return;
    opened.current = true;
    window.open(app.externalUrl, '_blank', 'noopener,noreferrer');
  }, [app.externalUrl]);

  return (
    <PageLayout title={app.displayName}>
      <SpaceBetween size="l">
        <Alert type="info" header={`${app.displayName} opens in a new tab`}>
          {app.displayName} is a separate application with its own sign-in — it does not run
          inside the platform. It should have opened in a new tab; if your browser blocked
          the pop-up, use the link below.
        </Alert>
        <Box>
          {app.externalUrl ? (
            <Link href={app.externalUrl} external target="_blank" rel="noopener noreferrer">
              Open {app.displayName}
            </Link>
          ) : null}
        </Box>
      </SpaceBetween>
    </PageLayout>
  );
}
